"use client";

import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { renderAsChild } from "@/components/ui/render-as-child";
import { cn } from "@/utils/lib";
import { resolveClassName } from "@/utils/resolve-class-name";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = ({
  asChild,
  children,
  nativeButton = false,
  render,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger> & {
  asChild?: boolean;
}) => (
  <SheetPrimitive.Trigger
    nativeButton={nativeButton}
    render={render ?? renderAsChild(asChild, children)}
    {...props}
  >
    {asChild ? null : children}
  </SheetPrimitive.Trigger>
);
SheetTrigger.displayName = "SheetTrigger";

const SheetClose = ({
  asChild,
  children,
  nativeButton = false,
  render,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close> & {
  asChild?: boolean;
}) => (
  <SheetPrimitive.Close
    nativeButton={nativeButton}
    render={render ?? renderAsChild(asChild, children)}
    {...props}
  >
    {asChild ? null : children}
  </SheetPrimitive.Close>
);
SheetClose.displayName = "SheetClose";

const SheetPortal = (
  props: React.ComponentProps<typeof SheetPrimitive.Portal>,
) => <SheetPrimitive.Portal {...props} />;

const SheetOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Backdrop>) => (
  <SheetPrimitive.Backdrop
    className={(state) =>
      cn(
        `
          fixed inset-0 z-100 bg-background/80 backdrop-blur-xs
          transition-opacity duration-200
          data-ending-style:opacity-0 data-starting-style:opacity-0
        `,
        resolveClassName(className, state),
      )
    }
    {...props}
  />
);
SheetOverlay.displayName = "SheetOverlay";

const sheetVariants = cva(
  `
    fixed z-100 gap-4 bg-background p-6 shadow-lg
    outline-hidden transition duration-300 ease-in-out
  `,
  {
    variants: {
      side: {
        top: `
          inset-x-0 top-0 border-b
          data-ending-style:-translate-y-10 data-ending-style:opacity-0
          data-starting-style:-translate-y-10 data-starting-style:opacity-0
        `,
        bottom: `
          inset-x-0 bottom-0 border-t
          data-ending-style:translate-y-10 data-ending-style:opacity-0
          data-starting-style:translate-y-10 data-starting-style:opacity-0
        `,
        left: `
          inset-y-0 left-0 h-full w-3/4 border-r
          data-ending-style:-translate-x-10 data-ending-style:opacity-0
          data-starting-style:-translate-x-10 data-starting-style:opacity-0
          sm:max-w-sm
        `,
        right: `
          inset-y-0 right-0 h-full w-3/4 border-l
          data-ending-style:translate-x-10 data-ending-style:opacity-0
          data-starting-style:translate-x-10 data-starting-style:opacity-0
          sm:max-w-sm
        `,
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface SheetContentProps
  extends
    React.ComponentProps<typeof SheetPrimitive.Popup>,
    VariantProps<typeof sheetVariants> {}

const SheetContent = ({
  ref,
  side = "right",
  className,
  children,
  ...props
}: SheetContentProps) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Popup
      ref={ref}
      className={(state) =>
        cn(sheetVariants({ side }), resolveClassName(className, state))
      }
      data-side={side}
      {...props}
    >
      {children}
      <SheetPrimitive.Close
        className="
          absolute
          top-4
          right-4
          rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2
          focus:ring-ring
          focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none
          data-popup-open:bg-secondary
        "
      >
        <Cross2Icon className="size-4" />
        <span className="sr-only">Close</span>
      </SheetPrimitive.Close>
    </SheetPrimitive.Popup>
  </SheetPortal>
);
SheetContent.displayName = "SheetContent";

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-2", className)} {...props} />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      `
        flex flex-col-reverse
        sm:flex-row sm:justify-end sm:space-x-2
      `,
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) => (
  <SheetPrimitive.Title
    ref={ref}
    className={(state) =>
      cn(
        "text-lg font-semibold text-foreground",
        resolveClassName(className, state),
      )
    }
    {...props}
  />
);
SheetTitle.displayName = "SheetTitle";

const SheetDescription = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) => (
  <SheetPrimitive.Description
    ref={ref}
    className={(state) =>
      cn("text-sm text-muted-foreground", resolveClassName(className, state))
    }
    {...props}
  />
);
SheetDescription.displayName = "SheetDescription";

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
